export const DURATION_FRAMES = 1199;

import React from "react";
import { AbsoluteFill, Audio, Sequence, staticFile, useCurrentFrame, interpolate, spring, useVideoConfig } from "remotion";
import { noise2D } from "@remotion/noise";
import { dark, fonts, safeZone, gradients } from "../styles/theme";

/*
 * Reel_cmohpp7gp00043qtj924xdg7k
 * Existes en la calle. En internet, no.
 * 1199 frames @ 30fps
 * Scene timings:
 *   Scene 1 "Hook"          f0    +89
 *   Scene 2 "Problema"      f89   +153
 *   Scene 3 "Dato"          f242  +151
 *   Scene 4 "Reframe"       f393  +76
 *   Scene 5 "Solución"      f469  +249
 *   Scene 6 "Prueba Social" f718  +166
 *   Scene 7 "CTA"           f884  +315
 */

const SPRING = { damping: 20, stiffness: 80, mass: 1 };
const W = 1080;
const H = 1920;

const FloatingParticles: React.FC = () => {
  const frame = useCurrentFrame();
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 16 }).map((_, i) => {
        const nx = noise2D("particle-x-" + i, frame * 0.008, 0);
        const ny = noise2D("particle-y-" + i, frame * 0.006, 0);
        const x = (i / 16) * W + nx * 120;
        const y = ((i * 137) % H) + ny * 90;
        const hue = 200 + ((i * 9) % 40);
        const size = 4 + ((i * 3) % 6);
        const op = 0.06 + ((i % 3) * 0.01);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: x,
              top: y,
              width: size,
              height: size,
              borderRadius: "50%",
              background: `hsla(${hue}, 90%, 70%, 1)`,
              opacity: op,
              filter: "blur(1px)",
              boxShadow: `0 0 12px hsla(${hue}, 90%, 70%, 0.6)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const LightSweep: React.FC = () => {
  const frame = useCurrentFrame();
  const left = interpolate(frame, [0, 1199], [-300, 1380], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ pointerEvents: "none", overflow: "hidden" }}>
      <div
        style={{
          position: "absolute",
          left,
          top: -200,
          width: 240,
          height: 2400,
          background: "linear-gradient(90deg, transparent, rgba(96,165,250,0.06), transparent)",
          transform: "skewX(-12deg)",
          filter: "blur(8px)",
        }}
      />
    </AbsoluteFill>
  );
};

const VignetteOverlay: React.FC = () => {
  const frame = useCurrentFrame();
  const opacity = interpolate(
    frame,
    [0, 89, 242, 393, 469, 718, 884, 1199],
    [0.35, 0.45, 0.55, 0.55, 0.4, 0.45, 0.45, 0.5],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <AbsoluteFill
      style={{
        pointerEvents: "none",
        background: `radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,${opacity}) 100%)`,
      }}
    />
  );
};

const SceneGlow: React.FC = () => {
  const frame = useCurrentFrame();
  const redOpacity = interpolate(
    frame,
    [0, 89, 200, 393, 469, 884, 1199],
    [0, 0.2, 0.55, 0.45, 0, 0.15, 0.2],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  const blueOpacity = interpolate(
    frame,
    [0, 89, 393, 469, 600, 884, 1050, 1199],
    [0.15, 0.05, 0.05, 0.4, 0.55, 0.6, 0.7, 0.55],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 700px 700px at 30% 35%, rgba(239,68,68,0.4), transparent)",
          opacity: redOpacity,
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(ellipse 800px 800px at 70% 65%, rgba(37,99,235,0.5), transparent)",
          opacity: blueOpacity,
        }}
      />
    </AbsoluteFill>
  );
};

const RisingBokeh: React.FC<{ active: number }> = ({ active }) => {
  const frame = useCurrentFrame();
  if (frame < active) return null;
  const local = frame - active;
  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {Array.from({ length: 18 }).map((_, i) => {
        const seed = (i * 73) % 1080;
        const cycle = 240;
        const t = (local + i * 12) % cycle;
        const y = interpolate(t, [0, cycle], [H + 40, -80], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const op = interpolate(t, [0, cycle * 0.2, cycle * 0.8, cycle], [0, 0.5, 0.5, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
        const size = 6 + ((i * 5) % 14);
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              left: seed,
              top: y,
              width: size,
              height: size,
              borderRadius: "50%",
              background: "rgba(96,165,250,0.7)",
              opacity: op,
              filter: "blur(2px)",
              boxShadow: "0 0 18px rgba(96,165,250,0.8)",
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

const ContinuousBackground: React.FC = () => {
  return (
    <AbsoluteFill>
      <AbsoluteFill style={{ background: gradients.dark }} />
      <SceneGlow />
      <FloatingParticles />
      <LightSweep />
      <RisingBokeh active={884} />
      <VignetteOverlay />
    </AbsoluteFill>
  );
};

const gradientText: React.CSSProperties = {
  background: "linear-gradient(135deg, #60a5fa, #2563eb, #818cf8)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
};

// ---------- SCENE 1 — HOOK ----------
const Scene1: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const enter = spring({ frame, fps, config: SPRING });
  const pushIn = interpolate(frame, [0, 89], [1, 1.08], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const goldenLight = interpolate(frame, [0, 60, 89], [0.7, 0.9, 0.5], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const phoneAppear = interpolate(frame, [60, 89], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const text1Y = interpolate(enter, [0, 1], [60, 0]);
  const text2Y = interpolate(spring({ frame: frame - 22, fps, config: SPRING }), [0, 1], [60, 0]);
  const text1Op = interpolate(frame, [8, 30], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const text2Op = interpolate(frame, [30, 52], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill>
      {/* Façade illustration */}
      <AbsoluteFill style={{ transform: `scale(${pushIn})`, transformOrigin: "50% 55%" }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="goldenSky" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a2a1a" />
              <stop offset="60%" stopColor="#1a1410" />
              <stop offset="100%" stopColor="#08090d" />
            </linearGradient>
            <linearGradient id="goldGlow" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f5b860" stopOpacity={goldenLight} />
              <stop offset="100%" stopColor="#c97a2a" stopOpacity={goldenLight * 0.6} />
            </linearGradient>
            <linearGradient id="window" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a2a18" />
              <stop offset="100%" stopColor="#1a1208" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#goldenSky)" />
          {/* Building */}
          <rect x="80" y="600" width="920" height="900" fill="#1a1612" />
          <rect x="80" y="600" width="920" height="60" fill="#2a1f15" />
          {/* Awning */}
          <path d="M 60,820 L 1020,820 L 1000,900 L 80,900 Z" fill="#5a3520" />
          <path d="M 60,820 L 1020,820 L 1000,900 L 80,900 Z" fill="url(#goldGlow)" opacity="0.4" />
          {/* Sign */}
          <rect x="160" y="700" width="760" height="100" fill="#2a1810" stroke="#8b5a2b" strokeWidth="2" />
          <text x="540" y="755" fontFamily={fonts.headline} fontSize="44" fontWeight="800" fill="#d4a356" textAnchor="middle" letterSpacing="6">
            MERCERÍA
          </text>
          <text x="540" y="785" fontFamily={fonts.body} fontSize="20" fontWeight="600" fill="#8b5a2b" textAnchor="middle" letterSpacing="4">
            DESDE 1998
          </text>
          {/* Window display */}
          <rect x="180" y="950" width="720" height="500" fill="url(#window)" stroke="#4a3520" strokeWidth="3" />
          <rect x="180" y="950" width="720" height="500" fill="url(#goldGlow)" opacity="0.25" />
          {/* Window mullions */}
          <line x1="540" y1="950" x2="540" y2="1450" stroke="#4a3520" strokeWidth="3" />
          <line x1="180" y1="1200" x2="900" y2="1200" stroke="#4a3520" strokeWidth="3" />
          {/* Spools inside */}
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              <circle cx={250 + i * 120} cy={1080} r="22" fill={["#8b3a3a", "#3a5a8b", "#8b6a3a", "#5a8b3a"][i]} opacity="0.7" />
              <circle cx={250 + i * 120} cy={1080} r="6" fill="#1a1208" />
            </g>
          ))}
          {/* Door */}
          <rect x="430" y="1100" width="220" height="350" fill="#0a0a0a" stroke="#5a3520" strokeWidth="3" />
          {/* Floor */}
          <rect x="0" y="1450" width={W} height="470" fill="#0a0907" />
          {/* Old lady silhouette */}
          <g transform="translate(700, 1280)" opacity={interpolate(frame, [0, 30, 89], [0, 1, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })}>
            <ellipse cx="0" cy="0" rx="28" ry="32" fill="#2a1f18" />
            <rect x="-30" y="20" width="60" height="100" rx="8" fill="#3a2a20" />
            <rect x="20" y="80" width="22" height="40" rx="4" fill="#5a3a25" />
          </g>
          {/* Light beam */}
          <path d="M 540,820 L 1080,1920 L 0,1920 Z" fill="url(#goldGlow)" opacity={goldenLight * 0.18} />
        </svg>
      </AbsoluteFill>

      {/* Phone overlay (transition) */}
      <AbsoluteFill style={{ opacity: phoneAppear }}>
        <div
          style={{
            position: "absolute",
            left: 280,
            top: 1100,
            width: 520,
            height: 110,
            borderRadius: 55,
            background: "rgba(255,255,255,0.95)",
            boxShadow: "0 0 80px rgba(96,165,250,0.4)",
            display: "flex",
            alignItems: "center",
            paddingLeft: 32,
            gap: 16,
          }}
        >
          <svg width="36" height="36" viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="7" stroke="#666" strokeWidth="2" fill="none" />
            <line x1="16" y1="16" x2="21" y2="21" stroke="#666" strokeWidth="2" />
          </svg>
          <div style={{ width: 2, height: 40, background: "#2563eb", opacity: (frame % 20) < 10 ? 1 : 0 }} />
        </div>
      </AbsoluteFill>

      {/* Text */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          justifyContent: "flex-start",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 80,
            fontWeight: 800,
            color: dark.text,
            textAlign: "center",
            lineHeight: 1.05,
            letterSpacing: -1,
            opacity: text1Op,
            transform: `translateY(${text1Y}px)`,
            textShadow: "0 4px 24px rgba(0,0,0,0.6)",
          }}
        >
          Llevas 12 años abierto.
        </div>
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 80,
            fontWeight: 800,
            textAlign: "center",
            lineHeight: 1.05,
            letterSpacing: -1,
            marginTop: 12,
            opacity: text2Op,
            transform: `translateY(${text2Y}px)`,
            textShadow: "0 4px 24px rgba(0,0,0,0.6)",
            ...gradientText,
          }}
        >
          Para Google, abriste ayer.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 2 — PROBLEMA ----------
const Scene2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const enter = spring({ frame, fps, config: SPRING });
  const phoneScale = interpolate(enter, [0, 1], [0.92, 1]);
  const cursorBlink = (frame % 24) < 12;
  const queryProgress = interpolate(frame, [10, 50], [0, 19], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const query = "mercería cerca de mí".slice(0, Math.floor(queryProgress));
  const resultsAppear = interpolate(frame, [55, 80], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const ourShopFade = interpolate(frame, [85, 130], [1, 0.15], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const text1 = spring({ frame: frame - 60, fps, config: SPRING });
  const text2 = spring({ frame: frame - 82, fps, config: SPRING });
  const text3 = spring({ frame: frame - 104, fps, config: SPRING });

  return (
    <AbsoluteFill>
      {/* Phone frame */}
      <AbsoluteFill style={{ transform: `scale(${phoneScale})`, transformOrigin: "50% 50%" }}>
        <div
          style={{
            position: "absolute",
            left: 90,
            top: 320,
            width: 900,
            height: 1380,
            borderRadius: 56,
            background: "rgba(20,22,30,0.85)",
            border: "2px solid rgba(255,255,255,0.08)",
            boxShadow: "0 0 100px rgba(37,99,235,0.15), inset 0 0 60px rgba(0,0,0,0.5)",
            overflow: "hidden",
          }}
        >
          {/* Search bar */}
          <div
            style={{
              margin: "60px 40px 30px",
              height: 80,
              borderRadius: 40,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.1)",
              display: "flex",
              alignItems: "center",
              padding: "0 24px",
              gap: 16,
            }}
          >
            <svg width="28" height="28" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" stroke="#9ca3af" strokeWidth="2" fill="none" />
              <line x1="16" y1="16" x2="21" y2="21" stroke="#9ca3af" strokeWidth="2" />
            </svg>
            <span style={{ fontFamily: fonts.body, fontSize: 32, color: "#e5e7eb", fontWeight: 500 }}>
              {query}
              <span style={{ opacity: cursorBlink ? 1 : 0, color: "#60a5fa", marginLeft: 2 }}>|</span>
            </span>
          </div>

          {/* Results */}
          <div style={{ padding: "0 40px", opacity: resultsAppear }}>
            {[
              { name: "Mercería La Aguja Dorada", reviews: "127", new: true },
              { name: "Hilos & Botones Premium", reviews: "89", new: true },
              { name: "Costura Express", reviews: "203", new: true },
            ].map((shop, i) => (
              <div
                key={i}
                style={{
                  marginBottom: 28,
                  padding: 24,
                  borderRadius: 20,
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  transform: `translateY(${interpolate(frame, [55 + i * 8, 80 + i * 8], [40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })}px)`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                  <div style={{ width: 80, height: 80, borderRadius: 12, background: `linear-gradient(135deg, hsl(${i * 80}, 60%, 40%), hsl(${i * 80 + 30}, 70%, 30%))` }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: fonts.body, fontSize: 28, fontWeight: 700, color: dark.text }}>{shop.name}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
                      {[0, 1, 2, 3, 4].map((s) => (
                        <span key={s} style={{ color: dark.starGold, fontSize: 22 }}>★</span>
                      ))}
                      <span style={{ fontFamily: fonts.body, fontSize: 22, color: dark.textSecondary, marginLeft: 6 }}>({shop.reviews})</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Our shop, faded out */}
            <div
              style={{
                marginTop: 16,
                padding: 18,
                borderRadius: 16,
                background: "rgba(239,68,68,0.06)",
                border: "1px dashed rgba(239,68,68,0.3)",
                opacity: ourShopFade,
                filter: `blur(${interpolate(frame, [85, 130], [0, 4], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })}px)`,
              }}
            >
              <div style={{ fontFamily: fonts.body, fontSize: 22, fontWeight: 600, color: "rgba(239,68,68,0.7)" }}>
                Mercería Desde 1998
              </div>
              <div style={{ fontFamily: fonts.body, fontSize: 18, color: dark.textTertiary, marginTop: 4 }}>
                — sin información —
              </div>
            </div>
          </div>
        </div>
      </AbsoluteFill>

      {/* Bottom text labels */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingBottom: safeZone.bottom - 280,
          justifyContent: "flex-end",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        {[
          { text: "0 reseñas", anim: text1 },
          { text: "0 historial", anim: text2 },
          { text: "0 autoridad", anim: text3 },
        ].map((item, i) => (
          <div
            key={i}
            style={{
              fontFamily: fonts.headline,
              fontSize: 56,
              fontWeight: 800,
              color: dark.text,
              opacity: item.anim,
              transform: `translateY(${interpolate(item.anim, [0, 1], [40, 0])}px)`,
              letterSpacing: -1,
              textShadow: "0 0 30px rgba(239,68,68,0.4)",
            }}
          >
            <span style={{ color: dark.accentRed }}>{item.text.split(" ")[0]}</span>
            <span style={{ marginLeft: 16 }}>{item.text.split(" ")[1]}</span>
          </div>
        ))}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 3 — DATO ----------
const Scene3: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const splitProgress = interpolate(frame, [0, 25], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const leftX = interpolate(splitProgress, [0, 1], [-200, 0]);
  const rightX = interpolate(splitProgress, [0, 1], [200, 0]);
  const text1 = spring({ frame: frame - 40, fps, config: SPRING });
  const text2 = spring({ frame: frame - 70, fps, config: SPRING });
  const pinPulse = 1 + Math.sin(frame * 0.15) * 0.1;

  return (
    <AbsoluteFill>
      {/* LEFT: old shop, warm */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          width: W / 2,
          height: H,
          transform: `translateX(${leftX}px)`,
          overflow: "hidden",
        }}
      >
        <svg width={W / 2} height={H} viewBox={`0 0 ${W / 2} ${H}`}>
          <defs>
            <linearGradient id="warm" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#3a2515" />
              <stop offset="100%" stopColor="#0f0a05" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={W / 2} height={H} fill="url(#warm)" />
          {/* Calendar 2014 */}
          <rect x="60" y="280" width="160" height="180" fill="#3a2a1a" stroke="#8b5a2b" strokeWidth="2" />
          <rect x="60" y="280" width="160" height="40" fill="#5a3a20" />
          <text x="140" y="308" fontFamily={fonts.body} fontSize="22" fontWeight="700" fill="#d4a356" textAnchor="middle">2014</text>
          {Array.from({ length: 6 }).map((_, r) =>
            Array.from({ length: 7 }).map((_, c) => (
              <circle key={`${r}-${c}`} cx={75 + c * 22} cy={340 + r * 18} r="4" fill="#8b5a2b" opacity="0.6" />
            ))
          )}
          {/* Old photo on wall */}
          <rect x="320" y="280" width="120" height="160" fill="#1a1208" stroke="#8b5a2b" strokeWidth="3" />
          <rect x="335" y="295" width="90" height="130" fill="#5a3a20" opacity="0.5" />
          {/* Wooden counter with hands */}
          <rect x="0" y="1100" width={W / 2} height="820" fill="#1a1208" />
          <rect x="0" y="1100" width={W / 2} height="40" fill="#3a2515" />
          {/* Spools/items */}
          {Array.from({ length: 6 }).map((_, i) => (
            <g key={i}>
              <rect x={40 + i * 80} y="1180" width="60" height="80" rx="6" fill={["#8b3a3a", "#3a5a8b", "#8b6a3a", "#5a8b3a", "#8b3a8b", "#5a3a20"][i]} opacity="0.85" />
              <rect x={40 + i * 80} y="1180" width="60" height="14" fill="#000" opacity="0.3" />
            </g>
          ))}
          {/* Hand silhouette (curtido) */}
          <g transform="translate(280, 1380)" opacity="0.9">
            <ellipse cx="0" cy="0" rx="80" ry="40" fill="#8b6a4a" />
            <ellipse cx="-30" cy="-20" rx="14" ry="35" fill="#8b6a4a" transform="rotate(-15)" />
            <ellipse cx="0" cy="-30" rx="14" ry="40" fill="#8b6a4a" />
            <ellipse cx="30" cy="-25" rx="14" ry="38" fill="#8b6a4a" transform="rotate(10)" />
          </g>
          {/* Warm ambient */}
          <rect x="0" y="0" width={W / 2} height={H} fill="#f5b860" opacity="0.06" />
        </svg>
      </div>

      {/* RIGHT: new shop, cool */}
      <div
        style={{
          position: "absolute",
          left: W / 2,
          top: 0,
          width: W / 2,
          height: H,
          transform: `translateX(${rightX}px)`,
          overflow: "hidden",
        }}
      >
        <svg width={W / 2} height={H} viewBox={`0 0 ${W / 2} ${H}`}>
          <defs>
            <linearGradient id="cool" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#0a1525" />
              <stop offset="100%" stopColor="#050810" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={W / 2} height={H} fill="url(#cool)" />
          {/* Modern wall */}
          <rect x="0" y="0" width={W / 2} height="1100" fill="#0f1a2a" />
          {/* Laptop */}
          <g transform="translate(140, 380)">
            <rect x="0" y="0" width="260" height="170" rx="8" fill="#1a2535" stroke="#2a3545" strokeWidth="2" />
            <rect x="12" y="12" width="236" height="146" fill="#0a1525" />
            {/* Modern web mockup */}
            <rect x="20" y="20" width="220" height="20" fill="#2563eb" opacity="0.4" />
            <rect x="20" y="50" width="100" height="60" fill="#60a5fa" opacity="0.3" />
            <rect x="130" y="50" width="110" height="14" fill="rgba(255,255,255,0.4)" />
            <rect x="130" y="72" width="90" height="10" fill="rgba(255,255,255,0.2)" />
            <rect x="130" y="88" width="100" height="10" fill="rgba(255,255,255,0.2)" />
            <rect x="20" y="120" width="220" height="30" fill="#2563eb" />
            <rect x="-20" y="170" width="300" height="14" fill="#2a3545" />
          </g>
          {/* Map with pin */}
          <g transform="translate(60, 1180)">
            <rect x="0" y="0" width="420" height="500" rx="20" fill="#0a1525" stroke="rgba(96,165,250,0.3)" strokeWidth="2" />
            {/* Map grid */}
            {Array.from({ length: 8 }).map((_, i) => (
              <line key={`h${i}`} x1="0" y1={60 * i} x2="420" y2={60 * i} stroke="rgba(96,165,250,0.1)" strokeWidth="1" />
            ))}
            {Array.from({ length: 7 }).map((_, i) => (
              <line key={`v${i}`} x1={60 * i} y1="0" x2={60 * i} y2="500" stroke="rgba(96,165,250,0.1)" strokeWidth="1" />
            ))}
            {/* Roads */}
            <path d="M 0,250 L 420,250" stroke="rgba(96,165,250,0.25)" strokeWidth="6" />
            <path d="M 200,0 L 200,500" stroke="rgba(96,165,250,0.25)" strokeWidth="6" />
            {/* Pin glowing */}
            <g transform={`translate(210, 240) scale(${pinPulse})`}>
              <circle cx="0" cy="0" r="60" fill="#39ff14" opacity="0.15" />
              <circle cx="0" cy="0" r="38" fill="#39ff14" opacity="0.25" />
              <path d="M 0,-24 C -14,-24 -24,-14 -24,0 C -24,16 0,32 0,32 C 0,32 24,16 24,0 C 24,-14 14,-24 0,-24 Z" fill="#22c55e" />
              <circle cx="0" cy="-4" r="8" fill="#fff" />
            </g>
          </g>
          {/* Cool ambient */}
          <rect x="0" y="0" width={W / 2} height={H} fill="#60a5fa" opacity="0.05" />
        </svg>
      </div>

      {/* Center divider */}
      <div
        style={{
          position: "absolute",
          left: W / 2 - 1,
          top: 0,
          width: 2,
          height: H,
          background: "linear-gradient(180deg, transparent, rgba(96,165,250,0.6), transparent)",
          opacity: splitProgress,
        }}
      />

      {/* Texts */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          justifyContent: "flex-start",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: 20,
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: dark.glowBlue,
            padding: "32px 44px",
            opacity: text1,
            transform: `translateY(${interpolate(text1, [0, 1], [40, 0])}px)`,
          }}
        >
          <div
            style={{
              fontFamily: fonts.headline,
              fontSize: 56,
              fontWeight: 800,
              color: dark.text,
              textAlign: "center",
              letterSpacing: -1,
              lineHeight: 1.05,
            }}
          >
            Él abrió hace 6 meses.
          </div>
        </div>

        <div
          style={{
            marginTop: 20,
            fontFamily: fonts.headline,
            fontSize: 88,
            fontWeight: 800,
            textAlign: "center",
            letterSpacing: -2,
            opacity: text2,
            transform: `translateY(${interpolate(text2, [0, 1], [40, 0])}px) scale(${interpolate(text2, [0, 1], [0.9, 1])})`,
            ...gradientText,
            filter: "drop-shadow(0 0 30px rgba(96,165,250,0.4))",
          }}
        >
          Sale primero.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 4 — REFRAME ----------
const Scene4: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const morph = interpolate(frame, [0, 50], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const enter = spring({ frame, fps, config: SPRING });
  const text2 = spring({ frame: frame - 25, fps, config: SPRING });

  return (
    <AbsoluteFill>
      {/* Aerial street morphing into screen */}
      <AbsoluteFill style={{ overflow: "hidden" }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="dusk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1a1525" />
              <stop offset="100%" stopColor="#08090d" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#dusk)" />

          {/* Cobble street → pixels */}
          {Array.from({ length: 8 }).map((_, r) =>
            Array.from({ length: 12 }).map((_, c) => {
              const cobble = `M ${c * 90 + 20},${800 + r * 80} l 70,0 l 0,60 l -70,0 z`;
              const pixel = `M ${c * 90 + 20},${800 + r * 80} l 70,0 l 0,60 l -70,0 z`;
              const fill = morph > 0.4
                ? `hsla(${210 + ((r + c) * 8) % 30}, 80%, ${30 + (((r + c) * 7) % 20)}%, ${0.6 + morph * 0.3})`
                : `hsla(30, 20%, ${15 + (((r + c) * 7) % 10)}%, 0.8)`;
              return <path key={`cobble-${r}-${c}`} d={morph > 0.5 ? pixel : cobble} fill={fill} stroke={morph > 0.5 ? "rgba(96,165,250,0.2)" : "#000"} strokeWidth="1" />;
            })
          )}

          {/* Awnings → tabs */}
          {[0, 1, 2].map((i) => (
            <g key={i} transform={`translate(${120 + i * 280}, ${500 - morph * 200})`}>
              {morph < 0.5 ? (
                <path d={`M 0,0 L 200,0 L 180,80 L 20,80 Z`} fill={["#8b3a3a", "#3a5a8b", "#8b6a3a"][i]} opacity={1 - morph * 1.5} />
              ) : (
                <rect x="0" y="0" width="200" height="50" rx="8 8 0 0" fill="rgba(96,165,250,0.2)" stroke="rgba(96,165,250,0.5)" strokeWidth="2" opacity={(morph - 0.5) * 2} />
              )}
            </g>
          ))}

          {/* Signs → search results */}
          {[0, 1, 2].map((i) => (
            <g key={`sr-${i}`} transform={`translate(140, ${1500 + i * 90})`} opacity={morph}>
              <rect x="0" y="0" width="800" height="70" rx="12" fill="rgba(255,255,255,0.06)" stroke="rgba(96,165,250,0.3)" strokeWidth="1" />
              <circle cx="35" cy="35" r="14" fill="#60a5fa" opacity="0.4" />
              <rect x="65" y="20" width="500" height="14" rx="2" fill="rgba(255,255,255,0.6)" />
              <rect x="65" y="42" width="350" height="10" rx="2" fill="rgba(255,255,255,0.3)" />
            </g>
          ))}
        </svg>
      </AbsoluteFill>

      {/* Big text */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 96,
            fontWeight: 800,
            color: dark.text,
            textAlign: "center",
            letterSpacing: -2,
            lineHeight: 1.0,
            opacity: enter,
            transform: `translateY(${interpolate(enter, [0, 1], [60, 0])}px)`,
            textShadow: "0 8px 40px rgba(0,0,0,0.8)",
          }}
        >
          Ahora la calle
        </div>
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 128,
            fontWeight: 800,
            textAlign: "center",
            letterSpacing: -3,
            lineHeight: 1.0,
            marginTop: 12,
            opacity: text2,
            transform: `translateY(${interpolate(text2, [0, 1], [60, 0])}px) scale(${interpolate(text2, [0, 1], [0.85, 1])})`,
            ...gradientText,
            filter: "drop-shadow(0 0 40px rgba(96,165,250,0.6))",
          }}
        >
          es Google.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 5 — SOLUCIÓN ----------
const Scene5: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const enter = spring({ frame, fps, config: SPRING });
  const tabletScale = interpolate(enter, [0, 1], [0.85, 1]);
  const dollyShift = interpolate(frame, [0, 249], [-10, 10], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const webAppear = interpolate(frame, [25, 60], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const businessAppear = interpolate(frame, [70, 110], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const reviewAppear = interpolate(frame, [120, 160], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  const text1 = spring({ frame: frame - 60, fps, config: SPRING });
  const text2 = spring({ frame: frame - 110, fps, config: SPRING });

  const stars = Math.min(5, Math.floor(interpolate(frame, [120, 200], [0, 5], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })));

  return (
    <AbsoluteFill>
      <AbsoluteFill style={{ transform: `translateX(${dollyShift}px) scale(${tabletScale})`, transformOrigin: "50% 60%" }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="sunlight" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f5dca0" stopOpacity="0.1" />
              <stop offset="100%" stopColor="transparent" />
            </linearGradient>
          </defs>
          {/* Shop interior */}
          <rect x="0" y="0" width={W} height="1100" fill="#1a1410" />
          <rect x="0" y="1100" width={W} height="820" fill="#0a0805" />
          {/* Door open with light */}
          <rect x="60" y="200" width="280" height="700" fill="#1a1208" />
          <rect x="80" y="220" width="240" height="660" fill="url(#sunlight)" />
          <rect x="80" y="220" width="240" height="660" fill="#f5dca0" opacity="0.15" />
          {/* Shelves */}
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <rect x="700" y={300 + i * 200} width="320" height="20" fill="#3a2515" />
              {Array.from({ length: 5 }).map((_, j) => (
                <rect key={j} x={720 + j * 60} y={250 + i * 200} width="50" height="60" rx="4" fill={["#8b3a3a", "#3a5a8b", "#8b6a3a", "#5a8b3a", "#5a3a20"][j]} opacity="0.7" />
              ))}
            </g>
          ))}
          {/* Owner figure */}
          <g transform="translate(540, 1000)">
            <ellipse cx="0" cy="-180" rx="55" ry="65" fill="#8b6a4a" />
            <rect x="-70" y="-120" width="140" height="200" rx="20" fill="#3a4a5a" />
            <rect x="-50" y="80" width="100" height="120" rx="10" fill="#1a2030" />
          </g>
        </svg>
      </AbsoluteFill>

      {/* Tablet */}
      <AbsoluteFill style={{ transform: `translate(${dollyShift * 1.2}px, 0) scale(${tabletScale})`, transformOrigin: "50% 50%" }}>
        <div
          style={{
            position: "absolute",
            left: 180,
            top: 700,
            width: 720,
            height: 540,
            borderRadius: 32,
            background: "#0f1525",
            border: "10px solid #1a2030",
            boxShadow: "0 30px 80px rgba(0,0,0,0.8), 0 0 80px rgba(37,99,235,0.25)",
            overflow: "hidden",
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Web mockup top */}
          <div
            style={{
              opacity: webAppear,
              transform: `translateY(${interpolate(webAppear, [0, 1], [20, 0])}px)`,
              height: 160,
              borderRadius: 12,
              background: "linear-gradient(135deg, rgba(37,99,235,0.2), rgba(96,165,250,0.1))",
              border: "1px solid rgba(96,165,250,0.3)",
              padding: 16,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: "#ef4444" }} />
              <div style={{ width: 8, height: 8, borderRadius: 4, background: "#f59e0b" }} />
              <div style={{ width: 8, height: 8, borderRadius: 4, background: "#22c55e" }} />
              <div style={{ marginLeft: 10, fontFamily: fonts.body, fontSize: 16, color: dark.textSecondary }}>merceriadelbarrio.es</div>
            </div>
            <div style={{ fontFamily: fonts.headline, fontSize: 28, fontWeight: 800, color: dark.text }}>Mercería Desde 1998</div>
            <div style={{ fontFamily: fonts.body, fontSize: 18, color: dark.textSecondary }}>Hilos · Botones · Mercería de barrio</div>
          </div>

          {/* Google Business */}
          <div
            style={{
              opacity: businessAppear,
              transform: `translateY(${interpolate(businessAppear, [0, 1], [20, 0])}px)`,
              flex: 1,
              borderRadius: 12,
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              padding: 16,
              display: "flex",
              gap: 14,
            }}
          >
            <div style={{ width: 130, height: 130, borderRadius: 10, background: "linear-gradient(135deg, #c97a2a, #f5b860)" }} />
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontFamily: fonts.body, fontSize: 22, fontWeight: 700, color: dark.text }}>Mercería Desde 1998</div>
              <div style={{ fontFamily: fonts.body, fontSize: 16, color: dark.textSecondary }}>4,9 ★ · Mercería</div>
              <div style={{ fontFamily: fonts.body, fontSize: 14, color: dark.textTertiary }}>Calle Mayor 24 · Abierto</div>
              <div style={{ display: "flex", gap: 6, marginTop: 4, opacity: reviewAppear }}>
                {[0, 1, 2, 3, 4].map((s) => (
                  <span key={s} style={{ color: s < stars ? dark.starGold : "rgba(255,255,255,0.15)", fontSize: 22 }}>★</span>
                ))}
                <span style={{ fontFamily: fonts.body, fontSize: 14, color: dark.textSecondary, marginLeft: 6, alignSelf: "center" }}>
                  ¡Excelente trato!
                </span>
              </div>
            </div>
          </div>
        </div>
      </AbsoluteFill>

      {/* Glass card with text */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: 24,
            border: "1px solid rgba(255,255,255,0.10)",
            boxShadow: dark.glowBlueStrong,
            padding: "36px 56px",
            opacity: text1,
            transform: `translateY(${interpolate(text1, [0, 1], [50, 0])}px)`,
            backdropFilter: "blur(10px)",
          }}
        >
          <div
            style={{
              fontFamily: fonts.headline,
              fontSize: 64,
              fontWeight: 800,
              textAlign: "center",
              letterSpacing: -1.5,
              lineHeight: 1.05,
              ...gradientText,
            }}
          >
            Web + SEO local
          </div>
          <div
            style={{
              fontFamily: fonts.headline,
              fontSize: 44,
              fontWeight: 700,
              color: dark.text,
              textAlign: "center",
              marginTop: 12,
              letterSpacing: -0.5,
              opacity: text2,
              transform: `translateY(${interpolate(text2, [0, 1], [20, 0])}px)`,
            }}
          >
            en <span style={{ color: dark.accentBlueMuted }}>3 meses</span>
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 6 — PRUEBA SOCIAL ----------
const Scene6: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 3 micro-shots of ~30 frames each
  const shot1On = frame >= 0 && frame < 30;
  const shot2On = frame >= 30 && frame < 60;
  const shot3On = frame >= 60 && frame < 95;
  const chartOn = frame >= 95;

  const text1 = spring({ frame: frame - 95, fps, config: SPRING });
  const text2 = spring({ frame: frame - 120, fps, config: SPRING });

  const chartProgress = interpolate(frame, [95, 165], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill>
      {/* Shot 1: Bakery */}
      {shot1On && (
        <AbsoluteFill
          style={{
            opacity: interpolate(frame, [0, 4, 26, 30], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            <rect x="0" y="0" width={W} height={H} fill="#1a1208" />
            <rect x="80" y="500" width="920" height="900" fill="#3a2515" />
            <rect x="200" y="600" width="680" height="80" fill="#5a3a20" />
            <text x="540" y="652" fontFamily={fonts.headline} fontSize="40" fontWeight="800" fill="#f5dca0" textAnchor="middle">PANADERÍA ARTESANA</text>
            {/* SERP banner top showing #1 */}
            <rect x="80" y="280" width="920" height="160" rx="20" fill="rgba(255,255,255,0.06)" stroke="rgba(96,165,250,0.4)" strokeWidth="2" />
            <circle cx="160" cy="360" r="32" fill="#22c55e" />
            <text x="160" y="372" fontFamily={fonts.body} fontSize="32" fontWeight="800" fill="#fff" textAnchor="middle">1</text>
            <text x="220" y="350" fontFamily={fonts.body} fontSize="28" fontWeight="700" fill="#fff">"pan artesano"</text>
            <text x="220" y="395" fontFamily={fonts.body} fontSize="22" fill="#60a5fa">→ Panadería Artesana</text>
            {/* Bread illustration */}
            {[0, 1, 2].map((i) => (
              <ellipse key={i} cx={300 + i * 240} cy="1100" rx="120" ry="60" fill="#c97a2a" />
            ))}
          </svg>
        </AbsoluteFill>
      )}

      {/* Shot 2: Mechanic */}
      {shot2On && (
        <AbsoluteFill
          style={{
            opacity: interpolate(frame, [30, 34, 56, 60], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            <rect x="0" y="0" width={W} height={H} fill="#0a1020" />
            {/* Map full of pins */}
            <rect x="80" y="280" width="920" height="900" rx="24" fill="#0f1a2a" stroke="rgba(96,165,250,0.3)" strokeWidth="2" />
            {Array.from({ length: 10 }).map((_, i) => (
              <line key={`h${i}`} x1="80" y1={280 + i * 90} x2="1000" y2={280 + i * 90} stroke="rgba(96,165,250,0.1)" />
            ))}
            {Array.from({ length: 10 }).map((_, i) => (
              <line key={`v${i}`} x1={80 + i * 92} y1="280" x2={80 + i * 92} y2="1180" stroke="rgba(96,165,250,0.1)" />
            ))}
            {/* Many review pins */}
            {Array.from({ length: 12 }).map((_, i) => {
              const px = 150 + ((i * 137) % 800);
              const py = 350 + ((i * 91) % 700);
              return (
                <g key={i} transform={`translate(${px}, ${py})`}>
                  <circle cx="0" cy="0" r="22" fill="#f59e0b" opacity="0.85" />
                  <text x="0" y="6" fontFamily={fonts.body} fontSize="18" fontWeight="800" fill="#fff" textAnchor="middle">★</text>
                </g>
              );
            })}
            <rect x="80" y="1240" width="920" height="100" rx="16" fill="rgba(255,255,255,0.06)" />
            <text x="540" y="1304" fontFamily={fonts.headline} fontSize="48" fontWeight="800" fill="#fff" textAnchor="middle">Taller Mecánico Vega</text>
          </svg>
        </AbsoluteFill>
      )}

      {/* Shot 3: Hairdresser */}
      {shot3On && (
        <AbsoluteFill
          style={{
            opacity: interpolate(frame, [60, 64, 88, 95], [0, 1, 1, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            <rect x="0" y="0" width={W} height={H} fill="#1a0a20" />
            <rect x="80" y="300" width="920" height="1100" rx="32" fill="rgba(255,255,255,0.05)" stroke="rgba(255,255,255,0.1)" />
            <text x="540" y="400" fontFamily={fonts.headline} fontSize="36" fontWeight="800" fill="#fff" textAnchor="middle">RESERVA ONLINE</text>
            {/* Booking slots */}
            {[
              { time: "10:00", label: "Disponible" },
              { time: "11:30", label: "Reservado", booked: true },
              { time: "13:00", label: "Disponible" },
              { time: "16:00", label: "Reservado", booked: true },
              { time: "17:30", label: "Reservado", booked: true },
            ].map((slot, i) => (
              <g key={i}>
                <rect x="140" y={480 + i * 130} width="800" height="100" rx="16" fill={slot.booked ? "rgba(96,165,250,0.18)" : "rgba(255,255,255,0.04)"} stroke={slot.booked ? "rgba(96,165,250,0.5)" : "rgba(255,255,255,0.1)"} strokeWidth="2" />
                <text x="180" y={540 + i * 130} fontFamily={fonts.body} fontSize="32" fontWeight="700" fill="#fff">{slot.time}</text>
                <text x="900" y={540 + i * 130} fontFamily={fonts.body} fontSize="24" fontWeight="600" fill={slot.booked ? "#60a5fa" : "rgba(255,255,255,0.5)"} textAnchor="end">{slot.label}</text>
              </g>
            ))}
          </svg>
        </AbsoluteFill>
      )}

      {/* Final chart */}
      {chartOn && (
        <AbsoluteFill
          style={{
            opacity: interpolate(frame, [95, 110], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
          }}
        >
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
            <defs>
              <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(96,165,250,0.5)" />
                <stop offset="100%" stopColor="rgba(96,165,250,0)" />
              </linearGradient>
            </defs>
            <rect x="0" y="0" width={W} height={H} fill="#0a1020" opacity="0.5" />
            {/* Local in background */}
            <rect x="60" y="600" width="960" height="600" fill="#1a1410" opacity="0.4" />
            <rect x="200" y="700" width="680" height="80" fill="#3a2a1a" opacity="0.6" />
            {/* Chart axes */}
            <line x1="120" y1="1300" x2="960" y2="1300" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            <line x1="120" y1="700" x2="120" y2="1300" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
            {/* Chart curve */}
            <path
              d={`M 120,1280 Q 300,1240 450,1140 T 700,950 T 960,${1280 - chartProgress * 600}`}
              fill="none"
              stroke="#60a5fa"
              strokeWidth="6"
              strokeDasharray="2000"
              strokeDashoffset={2000 - chartProgress * 2000}
              filter="drop-shadow(0 0 12px rgba(96,165,250,0.8))"
            />
            <path
              d={`M 120,1280 Q 300,1240 450,1140 T 700,950 T 960,${1280 - chartProgress * 600} L 960,1300 L 120,1300 Z`}
              fill="url(#chartFill)"
              opacity={chartProgress * 0.6}
            />
            {/* End dot */}
            <circle cx="960" cy={1280 - chartProgress * 600} r="14" fill="#60a5fa" filter="drop-shadow(0 0 16px rgba(96,165,250,1))" />
            <text x="540" y="780" fontFamily={fonts.body} fontSize="26" fontWeight="700" fill="rgba(255,255,255,0.5)" textAnchor="middle">VISITAS</text>
            <text x="940" y={1260 - chartProgress * 600} fontFamily={fonts.body} fontSize="32" fontWeight="800" fill="#60a5fa" textAnchor="end">+340%</text>
          </svg>
        </AbsoluteFill>
      )}

      {/* Texts */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          justifyContent: "flex-start",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 64,
            fontWeight: 800,
            color: dark.text,
            textAlign: "center",
            letterSpacing: -1.5,
            opacity: text1,
            transform: `translateY(${interpolate(text1, [0, 1], [40, 0])}px)`,
            textShadow: "0 4px 24px rgba(0,0,0,0.7)",
          }}
        >
          Negocios reales.
        </div>
        <div
          style={{
            fontFamily: fonts.headline,
            fontSize: 64,
            fontWeight: 800,
            textAlign: "center",
            letterSpacing: -1.5,
            marginTop: 8,
            opacity: text2,
            transform: `translateY(${interpolate(text2, [0, 1], [40, 0])}px)`,
            ...gradientText,
            filter: "drop-shadow(0 4px 24px rgba(0,0,0,0.7))",
          }}
        >
          Resultados reales.
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- SCENE 7 — CTA ----------
const Scene7: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Shutter rising
  const shutterY = interpolate(frame, [0, 80], [0, -1100], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  // Light blooming
  const dawnLight = interpolate(frame, [0, 100], [0.2, 0.9], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  // Domain typing
  const domain = "coredigital.es";
  const typeProgress = interpolate(frame, [110, 200], [0, domain.length], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const typed = domain.slice(0, Math.floor(typeProgress));
  const cursorBlink = (frame % 24) < 12;

  const enter = spring({ frame: frame - 90, fps, config: SPRING });
  const logoEnter = spring({ frame: frame - 220, fps, config: SPRING });

  return (
    <AbsoluteFill>
      {/* Background dawn */}
      <AbsoluteFill>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
          <defs>
            <linearGradient id="dawn" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2a1a30" />
              <stop offset="50%" stopColor="#5a3a20" />
              <stop offset="100%" stopColor="#1a1208" />
            </linearGradient>
            <radialGradient id="dawnSun" cx="50%" cy="80%" r="60%">
              <stop offset="0%" stopColor="#f5b860" stopOpacity={dawnLight} />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          <rect x="0" y="0" width={W} height={H} fill="url(#dawn)" />
          <rect x="0" y="0" width={W} height={H} fill="url(#dawnSun)" />

          {/* Shop façade */}
          <rect x="40" y="500" width="1000" height="1420" fill="#1a1208" />
          <rect x="40" y="500" width="1000" height="80" fill="#2a1a10" />

          {/* Inside (visible when shutter raised) */}
          <rect x="120" y="600" width="840" height="900" fill="#3a2515" />
          <rect x="120" y="600" width="840" height="900" fill="url(#dawnSun)" opacity="0.4" />

          {/* Shutter (descends from top, animates upward) */}
          <g transform={`translate(0, ${shutterY})`}>
            <rect x="120" y="600" width="840" height="900" fill="#2a2520" />
            {Array.from({ length: 28 }).map((_, i) => (
              <rect key={i} x="120" y={600 + i * 32} width="840" height="2" fill="#1a1410" />
            ))}
            <rect x="120" y="600" width="840" height="900" fill="#000" opacity="0.3" />
          </g>

          {/* Sidewalk */}
          <rect x="0" y="1500" width={W} height="420" fill="#0a0907" />
        </svg>
      </AbsoluteFill>

      {/* Domain typewriter card */}
      <AbsoluteFill
        style={{
          paddingLeft: safeZone.horizontal,
          paddingRight: safeZone.horizontal,
          paddingTop: safeZone.top,
          paddingBottom: safeZone.bottom,
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            background: "rgba(255,255,255,0.05)",
            borderRadius: 28,
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 0 100px rgba(37,99,235,0.35), inset 0 0 60px rgba(37,99,235,0.05)",
            padding: "48px 64px",
            backdropFilter: "blur(20px)",
            opacity: enter,
            transform: `translateY(${interpolate(enter, [0, 1], [40, 0])}px)`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 24,
          }}
        >
          {/* Logo mark */}
          <div
            style={{
              opacity: logoEnter,
              transform: `scale(${interpolate(logoEnter, [0, 1], [0.7, 1])})`,
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <svg width="56" height="56" viewBox="0 0 56 56">
              <defs>
                <linearGradient id="logoGrad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#60a5fa" />
                  <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
              </defs>
              <rect x="4" y="4" width="48" height="48" rx="14" fill="url(#logoGrad)" />
              <path d="M 18,20 L 18,36 M 18,20 L 30,20 M 18,28 L 28,28 M 18,36 L 30,36" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
              <circle cx="40" cy="28" r="5" fill="#fff" />
            </svg>
            <span style={{ fontFamily: fonts.headline, fontSize: 32, fontWeight: 800, color: dark.text, letterSpacing: -0.5 }}>
              koredigital
            </span>
          </div>

          {/* Domain */}
          <div
            style={{
              fontFamily: fonts.headline,
              fontSize: 96,
              fontWeight: 800,
              textAlign: "center",
              letterSpacing: -3,
              ...gradientText,
              filter: "drop-shadow(0 0 40px rgba(96,165,250,0.6))",
              minHeight: 110,
            }}
          >
            {typed}
            <span
              style={{
                opacity: cursorBlink && frame < 200 ? 1 : 0,
                color: "#60a5fa",
                WebkitTextFillColor: "#60a5fa",
                marginLeft: 4,
              }}
            >
              |
            </span>
          </div>

          {/* Subline */}
          <div
            style={{
              opacity: interpolate(frame, [220, 260], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
              fontFamily: fonts.body,
              fontSize: 32,
              fontWeight: 600,
              color: dark.textSecondary,
              textAlign: "center",
              letterSpacing: 0.5,
            }}
          >
            que entren por la pantalla
          </div>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ---------- ROOT ----------
const Reel_cmohpp7gp00043qtj924xdg7k: React.FC = () => {
  return (
    <AbsoluteFill style={{ background: dark.bg }}>
      <ContinuousBackground />

      <Sequence from={5}>
        <Audio src={staticFile("audio/cmohpp7gp00043qtj924xdg7k.mp3")} />
      </Sequence>

      <Sequence from={0} durationInFrames={89}>
        <Scene1 />
      </Sequence>
      <Sequence from={89} durationInFrames={153}>
        <Scene2 />
      </Sequence>
      <Sequence from={242} durationInFrames={151}>
        <Scene3 />
      </Sequence>
      <Sequence from={393} durationInFrames={76}>
        <Scene4 />
      </Sequence>
      <Sequence from={469} durationInFrames={249}>
        <Scene5 />
      </Sequence>
      <Sequence from={718} durationInFrames={166}>
        <Scene6 />
      </Sequence>
      <Sequence from={884} durationInFrames={315}>
        <Scene7 />
      </Sequence>
    </AbsoluteFill>
  );
};

export default Reel_cmohpp7gp00043qtj924xdg7k;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
